import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as api from "../../api/client";
import type { ApiCtx } from "../../api/client";
import type { CloudConfig } from "../../api/types";
import { useWorkspaces } from "../../state/workspaces";
import { toast } from "../../notify";
import { texte } from "../../texte";

/**
 * Konfig-Tab (nur Admin). Liest den aktuellen Stand über configure(ctx, {}) — der Server
 * merged leere overrides und gibt den vollen, unveränderten config zurück (botagent.py:_configure).
 * `expert_enabled` ist kein Server-Schlüssel (siehe state/workspaces.ts) und wird daher separat
 * als lokaler Schalter geführt, nicht als Teil des generischen config-Formulars.
 */
export function Config({ ctx }: { ctx: ApiCtx }) {
  const entry = useWorkspaces((s) => s.byName[ctx.workspace]);
  const setExpertEnabled = useWorkspaces((s) => s.setExpertEnabled);
  const query = useQuery({
    queryKey: ["config", ctx.apiUrl, ctx.token, ctx.workspace],
    queryFn: () => api.configure(ctx, {}),
  });
  const [draft, setDraft] = useState<CloudConfig | null>(null);

  useEffect(() => {
    if (query.data?.config) setDraft(query.data.config);
  }, [query.data]);

  if (!draft) return null;

  async function apply() {
    if (!draft) return;
    const res = await api.configure(ctx, draft);
    if (res.code < 0) {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
      return;
    }
    toast(texte.config.saved, texte.config.savedText, true);
  }

  return (
    <>
      <div className="cfg-table">
        <div className="rowi">
          <label>expert_enabled</label>
          <label className="switch">
            <input
              type="checkbox"
              checked={entry?.expertEnabled ?? false}
              onChange={(e) => setExpertEnabled(ctx.workspace, e.target.checked)}
              aria-label="expert_enabled"
            />
            <span className="tr" />
          </label>
        </div>
        {Object.entries(draft).map(([k, v]) => (
          <div className="rowi" key={k}>
            <label>{k}</label>
            {k === "language" ? (
              <select
                className="input"
                style={{ width: "100%" }}
                value={String(v)}
                onChange={(e) => setDraft((d) => (d ? { ...d, [k]: e.target.value } : d))}
              >
                <option value="de">de</option>
                <option value="en">en</option>
              </select>
            ) : k === "model" || k === "subagent_model" ? (
              <select
                className="input"
                style={{ width: "100%" }}
                value={String(v)}
                onChange={(e) => setDraft((d) => (d ? { ...d, [k]: e.target.value } : d))}
              >
                {window.config?.models?.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : typeof v === "boolean" ? (
              <label className="switch">
                <input
                  type="checkbox"
                  checked={v}
                  onChange={(e) => setDraft((d) => (d ? { ...d, [k]: e.target.checked } : d))}
                  aria-label={k}
                />
                <span className="tr" />
              </label>
            ) : (
              <input
                type="text"
                // Objektwertige Schlüssel (aktuell nur effort_models) als JSON anzeigen/bearbeiten —
                // String(v) würde sie zu "[object Object]" machen (validate_config lehnt das ab).
                value={typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}
                disabled={k === "workspace"}
                onChange={(e) => {
                  const raw = e.target.value;
                  let parsed: unknown = raw;
                  if (typeof v === "object" && v !== null) {
                    try {
                      parsed = JSON.parse(raw);
                    } catch {
                      parsed = raw;
                    }
                  }
                  setDraft((d) => (d ? { ...d, [k]: parsed } : d));
                }}
              />
            )}
          </div>
        ))}
      </div>
      <p className="hint" style={{ margin: "14px 0" }}>
        {texte.config.applyHint}
      </p>
      <button className="btn primary" onClick={apply}>
        {texte.config.apply}
      </button>
    </>
  );
}
